import React, { useState } from 'react';
import { Phone, Star, Check, MapPin, Mail, User, Home, MessageSquare, Award, Shield, Clock, Instagram, Facebook } from 'lucide-react';
import { Button } from './components/ui/button';
import { Card, CardContent } from './components/ui/card';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import { toast } from 'sonner';
import {
  mockServices,
  mockTestimonials,
  projectGallery,
  mockFAQs,
  businessInfo,
  submitContactForm
} from './mock/mockData';
import * as LucideIcons from 'lucide-react';
import './App.css';

function App() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const result = await submitContactForm(formData);
      if (result.success) {
        toast.success(result.message);
        setFormData({ name: '', phone: '', address: '', service: '', message: '' });
      }
    } catch (error) {
      toast.error('Something went wrong. Please call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCallClick = () => {
    window.location.href = `tel:${businessInfo.phone}`;
  };

  const scrollToQuote = () => {
    document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="app-container">
      {/* Sticky Header */}
      <header className="sticky-header">
        <div className="header-content">
          <div className="logo-section">
            <img 
              src="/images/logo-high-res.png" 
              alt="Better Lux Works - Lawn Service" 
              className="header-logo"
            />
          </div>
          <div className="header-actions">
            <div className="social-links-header">
              <a 
                href="https://instagram.com/betterluxlawnservices" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-link-header"
                aria-label="Follow us on Instagram"
              >
                <Instagram className="social-icon-header" />
              </a>
              <a 
                href="https://www.facebook.com/share/1Z9fTbHU1o/?mibextid=wwXIfr" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-link-header"
                aria-label="Follow us on Facebook"
              >
                <Facebook className="social-icon-header" />
              </a>
            </div>
            <a href={`tel:${businessInfo.phone}`} className="header-phone-link">
              <Phone className="phone-icon" />
              <span className="phone-number">{businessInfo.phone}</span>
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section - Conversion Optimized */}
      <section className="hero-section-v2">
        <div className="hero-background" style={{backgroundImage: 'url(/images/premium-lawn-1.jpg)'}}></div>
        <div className="hero-overlay-v2"></div>
        <div className="hero-content-v2">
          <div className="rating-badge-hero">
            <div className="stars-inline">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="star-small" fill="currentColor" />
              ))}
            </div>
            <span className="rating-text-hero">5.0 Star Rated Service</span>
          </div>
          
          <h1 className="hero-headline">
            Luxury Lawn Care in<br />South Whidbey Island
          </h1>
          
          <p className="hero-subheadline">
            Premium mowing, precision edging, and expert lawn maintenance from a locally owned 5-star service
          </p>
          
          <div className="hero-cta-buttons">
            <Button onClick={handleCallClick} size="lg" className="cta-primary-yellow">
              <Phone className="btn-icon" />
              Call {businessInfo.phone}
            </Button>
            <Button onClick={scrollToQuote} size="lg" variant="outline" className="cta-secondary-white">
              Get Free Quote
            </Button>
          </div>

          <div className="trust-bullets">
            <div className="trust-bullet">
              <Check className="check-icon" />
              <span>Island Owned & Operated</span>
            </div>
            <div className="trust-bullet">
              <Check className="check-icon" />
              <span>Licensed & Insured</span>
            </div>
            <div className="trust-bullet">
              <Check className="check-icon" />
              <span>Trusted by Your Neighbors</span>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="trust-bar">
        <div className="trust-bar-content">
          <div className="trust-item-bar">
            <Award className="trust-icon-bar" />
            <span>Professional Equipment</span>
          </div>
          <div className="trust-divider"></div>
          <div className="trust-item-bar">
            <Shield className="trust-icon-bar" />
            <span>Reliable Service</span>
          </div>
          <div className="trust-divider"></div>
          <div className="trust-item-bar">
            <Clock className="trust-icon-bar" />
            <span>Fast Response</span>
          </div>
        </div>
      </section>

      {/* Services Section - Benefit Driven */}
      <section className="services-section-v2">
        <div className="section-container">
          <div className="section-header-v2">
            <h2 className="section-title-v2">Professional Lawn Care Services</h2>
            <p className="section-subtitle-v2">
              Every lawn deserves the Better Lux treatment
            </p>
          </div>
          
          <div className="services-grid-v2">
            {mockServices.map((service) => {
              const IconComponent = LucideIcons[service.icon];
              return (
                <Card key={service.id} className="service-card-v2">
                  <CardContent className="service-content-v2">
                    <div className="service-icon-wrapper-v2">
                      {IconComponent && <IconComponent className="service-icon-v2" />}
                    </div>
                    <h3 className="service-title-v2">{service.title}</h3>
                    <p className="service-description-v2">{service.description}</p>
                    <button onClick={handleCallClick} className="service-cta-link">
                      Call now for a free quote →
                    </button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="cta-after-section">
            <Button onClick={handleCallClick} size="lg" className="cta-primary-yellow">
              <Phone className="btn-icon" />
              Call Now - {businessInfo.phone}
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="why-section">
        <div className="section-container">
          <div className="why-content">
            <div className="why-text">
              <h2 className="why-title">Why South Whidbey Homeowners Choose Better Lux</h2>
              <div className="why-features">
                <div className="why-feature">
                  <Check className="why-check" />
                  <div>
                    <h3 className="why-feature-title">Precision Striping</h3>
                    <p className="why-feature-text">Professional mowing patterns that make your lawn stand out</p>
                  </div>
                </div>
                <div className="why-feature">
                  <Check className="why-check" />
                  <div>
                    <h3 className="why-feature-title">Detail-Oriented Edging</h3>
                    <p className="why-feature-text">Clean, crisp borders that define your property beautifully</p>
                  </div>
                </div>
                <div className="why-feature">
                  <Check className="why-check" />
                  <div>
                    <h3 className="why-feature-title">Reliable Service</h3>
                    <p className="why-feature-text">We show up on time, every time. Consistent quality you can count on</p>
                  </div>
                </div>
                <div className="why-feature">
                  <Check className="why-check" />
                  <div>
                    <h3 className="why-feature-title">Island Roots</h3>
                    <p className="why-feature-text">We live and work here. Your satisfaction is our reputation in the community.</p>
                  </div>
                </div>
              </div>
              <Button onClick={handleCallClick} size="lg" className="cta-primary-yellow mt-8">
                <Phone className="btn-icon" />
                Get Your Free Quote
              </Button>
            </div>
            <div className="why-image">
              <img src="/images/project-1.jpg" alt="Professional lawn care in South Whidbey Island" className="why-img" />
            </div>
          </div>
        </div>
      </section>

      {/* Project Gallery */}
      <section className="gallery-section-v2">
        <div className="section-container">
          <div className="section-header-v2">
            <h2 className="section-title-v2">Your Neighbors Trust Better Lux</h2>
            <p className="section-subtitle-v2">Real results from South Whidbey Island properties</p>
          </div>
          <div className="project-gallery-grid-v2">
            {projectGallery.map((project) => (
              <div key={project.id} className="gallery-item-v2">
                <img 
                  src={project.image} 
                  alt={project.alt}
                  className="gallery-image-v2"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
          <div className="cta-after-section">
            <Button onClick={scrollToQuote} size="lg" className="cta-primary-yellow">
              Get Your Free Quote
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials - Social Proof */}
      <section className="testimonials-section-v2">
        <div className="section-container">
          <div className="section-header-v2">
            <div className="rating-display-v2">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="rating-star-v2" fill="currentColor" />
              ))}
            </div>
            <h2 className="section-title-v2">Highly Rated by {businessInfo.reviewCount} Happy Customers</h2>
            <p className="section-subtitle-v2">See why your neighbors love Better Lux</p>
          </div>
          <div className="testimonials-grid-v2">
            {mockTestimonials.map((testimonial) => (
              <Card key={testimonial.id} className="testimonial-card-v2">
                <CardContent className="testimonial-content-v2">
                  <div className="testimonial-stars-v2">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="testimonial-star-v2" fill="currentColor" />
                    ))}
                  </div>
                  <p className="testimonial-text-v2">"{testimonial.text}"</p>
                  <div className="testimonial-author-v2">
                    <p className="author-name-v2">{testimonial.name}</p>
                    <p className="author-location-v2">{testimonial.location}, WA</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="cta-after-section">
            <Button onClick={handleCallClick} size="lg" className="cta-primary-yellow">
              <Phone className="btn-icon" />
              Call {businessInfo.phone}
            </Button>
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="service-area-section">
        <div className="section-container">
          <div className="service-area-content">
            <MapPin className="location-icon-large" />
            <h2 className="service-area-title">Proudly Serving South Whidbey Island</h2>
            <p className="service-area-text">
              Including Langley, Freeland, Clinton, and surrounding areas
            </p>
            <Button onClick={handleCallClick} size="lg" className="cta-primary-yellow">
              <Phone className="btn-icon" />
              Call for Service
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Form - Conversion Focused */}
      <section id="contact" className="contact-section-v2">
        <div className="section-container">
          <div className="contact-wrapper">
            <div className="contact-info-v2">
              <h2 className="contact-title-v2">Get Your Free Quote</h2>
              <p className="contact-description-v2">
                Fill out the form and we'll call you within 24 hours, or call us now at:
              </p>
              <a href={`tel:${businessInfo.phone}`} className="contact-phone-large">
                <Phone className="phone-icon-large" />
                {businessInfo.phone}
              </a>
              <div className="contact-features">
                <div className="contact-feature">
                  <Check className="feature-check-v2" />
                  <span>Free, no-obligation quotes</span>
                </div>
                <div className="contact-feature">
                  <Check className="feature-check-v2" />
                  <span>Same-day response</span>
                </div>
                <div className="contact-feature">
                  <Check className="feature-check-v2" />
                  <span>Island owned & operated</span>
                </div>
              </div>
            </div>
            
            <Card className="contact-form-card-v2">
              <CardContent className="contact-form-content-v2">
                <form onSubmit={handleSubmit} className="contact-form-v2">
                  <div className="form-row">
                    <div className="form-group-v2">
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Your Name"
                        required
                        className="form-input-v2"
                      />
                    </div>
                    <div className="form-group-v2">
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="Phone Number"
                        required
                        className="form-input-v2"
                      />
                    </div>
                  </div>
                  <div className="form-group-v2">
                    <Input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="Property Address in South Whidbey"
                      required
                      className="form-input-v2"
                    />
                  </div>
                  <div className="form-group-v2">
                    <Input
                      type="text"
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                      placeholder="Service Needed (e.g., Weekly Mowing, One-time Cleanup)"
                      required
                      className="form-input-v2"
                    />
                  </div>
                  <div className="form-group-v2">
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Additional details about your lawn..."
                      rows={3}
                      className="form-textarea-v2"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    size="lg" 
                    className="submit-button-v2"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : 'Request Free Quote'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Social Media Trust Section */}
      <section className="social-trust-section">
        <div className="section-container">
          <div className="social-trust-content">
            <h2 className="social-trust-title">See Our Work in South Whidbey Island</h2>
            <p className="social-trust-text">
              Follow us on Instagram and Facebook to see our latest lawn transformations, before & after photos, and satisfied customers across the island
            </p>
            <div className="social-links-trust">
              <a 
                href="https://instagram.com/betterluxlawnservices" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-button-instagram"
              >
                <Instagram className="social-btn-icon" />
                <span>Follow on Instagram</span>
              </a>
              <a 
                href="https://www.facebook.com/share/1Z9fTbHU1o/?mibextid=wwXIfr" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-button-facebook"
              >
                <Facebook className="social-btn-icon" />
                <span>Follow on Facebook</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer-v2">
        <div className="footer-content-v2">
          <div className="footer-main">
            <img 
              src="/images/logo-high-res.png" 
              alt="Better Lux Works - Lawn Service" 
              className="footer-logo-v2"
            />
            <p className="footer-tagline-v2">Your lawn, but better.</p>
            <div className="footer-rating-v2">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="footer-star-v2" fill="currentColor" />
              ))}
              <span className="footer-rating-text">5.0 Star Rated</span>
            </div>
            <div className="footer-social-section">
              <p className="footer-social-label">Follow Better Lux Lawn Care</p>
              <div className="footer-social-links">
                <a 
                  href="https://instagram.com/betterluxlawnservices" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="footer-social-link"
                  aria-label="Follow us on Instagram"
                >
                  <Instagram className="footer-social-icon" />
                </a>
                <a 
                  href="https://www.facebook.com/share/1Z9fTbHU1o/?mibextid=wwXIfr" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="footer-social-link"
                  aria-label="Follow us on Facebook"
                >
                  <Facebook className="footer-social-icon" />
                </a>
              </div>
            </div>
          </div>
          <div className="footer-contact-v2">
            <h4 className="footer-heading-v2">Contact</h4>
            <a href={`tel:${businessInfo.phone}`} className="footer-phone-v2">
              <Phone className="footer-icon-v2" />
              {businessInfo.phone}
            </a>
            <p className="footer-location-v2">
              <MapPin className="footer-icon-v2" />
              {businessInfo.location}
            </p>
          </div>
        </div>
        <div className="footer-bottom-v2">
          <p className="footer-copyright-v2">
            © 2025 {businessInfo.name}. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Mobile Sticky Call Button */}
      <div className="mobile-call-button-v2">
        <Button onClick={handleCallClick} size="lg" className="mobile-cta-v2">
          <Phone className="btn-icon" />
          Call Now
        </Button>
      </div>
    </div>
  );
}

export default App;
